import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, Platform } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { Ionicons } from '@expo/vector-icons';
import { useInterestCatalog } from '../hooks/useInterestCatalog';
import { isCatalogInterest } from '../utils/interestUtils';

// Re-export for backward compatibility
export { DEFAULT_INTERESTS as INTERESTS } from '../constants/defaultInterests';

/**
 * Reusable multi-select interest chip picker.
 * Props:
 *   selected  — string[]   currently selected interest IDs + any custom strings
 *   onChange  — (ids: string[]) => void
 *   label     — optional heading string (defaults to "Your Interests")
 */
export function InterestsPicker({ selected = [], onChange, label = 'Your Interests' }) {
  const { isDarkMode } = useTheme();
  const { interests: catalogInterests } = useInterestCatalog();
  const [customInput, setCustomInput] = useState('');

  const customInterests = selected.filter((s) => !isCatalogInterest(s, catalogInterests));

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((i) => i !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const addCustom = () => {
    const trimmed = customInput.trim();
    if (!trimmed || selected.includes(trimmed)) {
      setCustomInput('');
      return;
    }
    onChange([...selected, trimmed]);
    setCustomInput('');
  };

  const removeCustom = (val) => {
    onChange(selected.filter((i) => i !== val));
  };

  const borderColor   = isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.15)';
  const labelColor    = isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';
  const chipTextColor = isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)';
  const inputBg       = isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';
  const inputText     = isDarkMode ? '#fff' : '#111';
  const placeholderColor = isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)';

  return (
    <View style={styles.wrapper}>
      <Text style={[styles.heading, { color: labelColor, borderBottomColor: borderColor }]}>
        {label}
      </Text>
      <View style={styles.grid}>
        {catalogInterests.map(({ id, label: name, emoji }) => {
          const active = selected.includes(id);
          return (
            <TouchableOpacity
              key={id}
              onPress={() => toggle(id)}
              activeOpacity={0.75}
              style={[
                styles.chip,
                active
                  ? styles.chipActive
                  : { borderColor, backgroundColor: 'transparent' },
              ]}
            >
              {emoji ? <Text style={styles.chipEmoji}>{emoji}</Text> : null}
              <Text style={[styles.chipLabel, active ? styles.chipLabelActive : { color: chipTextColor }]}>
                {name}
              </Text>
            </TouchableOpacity>
          );
        })}

        {customInterests.map((val) => (
          <TouchableOpacity
            key={val}
            onPress={() => removeCustom(val)}
            activeOpacity={0.75}
            style={[styles.chip, styles.chipActive, { paddingRight: 8 }]}
          >
            <Text style={[styles.chipLabel, styles.chipLabelActive]}>{val}</Text>
            <Ionicons name="close" size={12} color="#fff" style={{ marginLeft: 2 }} />
          </TouchableOpacity>
        ))}
      </View>

      <View style={[styles.addRow, { borderColor }]}>
        <TextInput
          value={customInput}
          onChangeText={setCustomInput}
          placeholder="Add your own…"
          placeholderTextColor={placeholderColor}
          onSubmitEditing={addCustom}
          returnKeyType="done"
          style={[styles.addInput, { backgroundColor: inputBg, color: inputText, borderColor }]}
          {...(Platform.OS === 'web' && { outline: 'none' })}
        />
        <TouchableOpacity
          onPress={addCustom}
          disabled={!customInput.trim()}
          style={[styles.addBtn, { opacity: customInput.trim() ? 1 : 0.4 }]}
        >
          <Ionicons name="add" size={18} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    marginTop: 24,
    marginBottom: 28,
  },
  heading: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    paddingBottom: 10,
    marginBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderRadius: 20,
    paddingVertical: 5,
    paddingHorizontal: 11,
    gap: 4,
  },
  chipActive: {
    backgroundColor: '#14b8a6',
    borderColor: '#14b8a6',
  },
  chipEmoji: {
    fontSize: 13,
  },
  chipLabel: {
    fontSize: 12,
    fontWeight: '500',
  },
  chipLabelActive: {
    color: '#ffffff',
  },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    gap: 8,
  },
  addInput: {
    flex: 1,
    borderWidth: 1.5,
    borderRadius: 20,
    paddingVertical: 5,
    paddingHorizontal: 12,
    fontSize: 12,
    fontWeight: '500',
  },
  addBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#14b8a6',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
